// src/components/Terms.tsx - Terms of Use display component
import React from 'react';
import PageContainer from './PageContainer';

interface TermsProps {
  onBack: () => void;
}

const Terms: React.FC<TermsProps> = ({ onBack }) => {
  return (
    <PageContainer 
      title="Terms of Use – API & Platform" 
      onBack={onBack}
    >
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>By accessing or using our API or platform ("Service"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree, do not access or use the Service.</p>
          </section>
          <section>
            <h2>2. License and Access</h2>
            <p>We grant you a limited, revocable, non-exclusive, non-transferable license to access and use the Service solely for your internal business purposes, subject to these Terms. You may not resell, redistribute, sublicense, or otherwise make the Service available to third parties without our prior written consent.</p>
          </section>
          <section>
            <h2>3. Restrictions on Use</h2>
            <p>You shall not (a) reverse engineer, decompile, or disassemble the Service; (b) interfere with or disrupt the integrity or performance of the Service; (c) use the Service for competitive benchmarking; (d) use automated tools to extract or scrape data unless explicitly permitted; (e) bypass rate limits or misuse authentication mechanisms; or (f) submit or upload unlawful, infringing, or harmful content.</p>
          </section>
          <section>
            <h2>4. API Credentials and Security</h2>
            <p>You must keep your API credentials secure and confidential. You are responsible for all activity under your account. You must notify us immediately of any unauthorized use or breach of security.</p>
          </section>
          <section>
            <h2>5. Rate Limits and Fair Use</h2>
            <p>We may apply usage limits to the Service as outlined in our documentation or pricing page. We reserve the right to throttle, suspend, or revoke access for excessive or abusive usage.</p>
          </section>
          <section>
            <h2>6. Monitoring and Audit Rights</h2>
            <p>We may monitor your use of the Service to ensure compliance with these Terms. You agree to cooperate with any audit or investigation into your usage.</p>
          </section>
          <section>
            <h2>7. Billing and Payment</h2>
            <p>You agree to pay all applicable fees based on your metered usage. Fees are billed monthly in arrears or as otherwise specified. All payments are non-refundable. We may suspend access for late or failed payments.</p>
          </section>
          <section>
            <h2>8. Pricing and Changes</h2>
            <p>We may change our pricing or introduce new fees with at least 30 days' notice. Your continued use after the effective date constitutes your acceptance.</p>
          </section>
          <section>
            <h2>9. Intellectual Property</h2>
            <p>We retain all rights, title, and interest in the Service, including any underlying software, models, data, and analytics. You may not copy, modify, or create derivative works. You grant us a royalty-free license to use feedback you submit for any purpose.</p>
          </section>
          <section>
            <h2>10. Privacy and Data Usage</h2>
            <p>Our Privacy Policy governs our collection and use of personal data. You are responsible for ensuring any data you provide complies with applicable data protection laws. You must obtain all necessary consents for any personal data submitted through the Service.</p>
          </section>
          <section>
            <h2>11. Export Control and Compliance</h2>
            <p>You may not use or access the Service if you are located in a jurisdiction subject to U.S. sanctions or export restrictions. You agree to comply with all applicable export control laws and regulations.</p>
          </section>
          <section>
            <h2>12. Termination</h2>
            <p>We may suspend or terminate your access at any time for any violation of these Terms or applicable law. You may terminate by ceasing use of the Service. Upon termination, all rights granted to you will immediately cease.</p>
          </section>
          <section>
            <h2>13. Indemnification</h2>
            <p>You agree to indemnify, defend, and hold harmless us and our affiliates, officers, and employees from any claims, losses, or liabilities arising from your use of the Service, your violation of these Terms, or your infringement of any third-party rights.</p>
          </section>
          <section>
            <h2>14. Disclaimers</h2>
            <p>The Service is provided "AS IS" and "AS AVAILABLE." We make no warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, non-infringement, or availability.</p>
          </section>
          <section>
            <h2>15. Limitation of Liability</h2>
            <p>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits or revenues. Our total liability for any claim shall not exceed the amount paid by you for the Service in the 30 days prior to the event giving rise to the claim.</p>
          </section>
          <section>
            <h2>16. Modifications to Terms</h2>
            <p>We may modify these Terms at any time. If changes are material, we will notify you via email or through the Service. Your continued use constitutes acceptance of the modified Terms.</p>
          </section>
          <section>
            <h2>17. Assignment</h2>
            <p>You may not assign or transfer your rights under these Terms without our prior written consent. We may assign this agreement freely, including in connection with a merger or sale.</p>
          </section>
          <section>
            <h2>18. Force Majeure</h2>
            <p>We are not liable for delays or failures to perform due to causes beyond our reasonable control, including natural disasters, internet outages, or governmental action.</p>
          </section>
          <section>
            <h2>19. Relationship of the Parties</h2>
            <p>Nothing in these Terms creates a partnership, agency, or employment relationship between you and us.</p>
          </section>
          <section>
            <h2>20. Governing Law and Dispute Resolution</h2>
            <p>These Terms are governed by the laws of Delaware, excluding conflicts of law principles. Any disputes shall be resolved exclusively in the courts of Delaware.</p>
          </section>
          <section>
            <h2>21. Notices</h2>
            <p>All notices must be sent to us at joe@startwave.io. We may provide notices to you through the Service or the contact email on file.</p>
          </section>
          <section>
            <h2>22. Reservation of Rights and No Waiver</h2>
            <p>We reserve all rights not expressly granted in these Terms. Any failure to enforce a provision of these Terms shall not constitute a waiver of such right.</p>
          </section>
          <section>
            <h2>23. Publicity</h2>
            <p>You may not use our name, logo, or trademarks in any manner without our prior written consent. You agree not to issue press releases or public statements regarding your use of the Service without approval.</p>
          </section>
          <section>
            <h2>24. Feedback</h2>
            <p>If you provide feedback or suggestions about the Service, you grant us an unrestricted, irrevocable, worldwide license to use that feedback without compensation or obligation.</p>
          </section>
    </PageContainer>
  );
};

export default Terms; 